import { TextFieldWithCheckbox } from "@/controls/form-fields/CheckboxField";
import { PhaseEventType } from "@/pages/eaf/data-entry/models/eaf.models";
import { upsertPhaseEventType } from "@/pages/eaf/data-entry/repositories/eaf.repositories";
import { useModalStyles } from "@/pages/laboratory/styles/modalStyles";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	TextField,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

type Props = {
	show: boolean;
	onHide: () => void;
	refreshData: Function;
	phaseEvent?: PhaseEventType | null;
};
export const UpsertPhaseEvent = ({
	show,
	onHide,
	refreshData,
	phaseEvent,
}: Props) => {
	const classes = useModalStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<PhaseEventType>({
		defaultValues: {
			PhaseEventTypeId: 0,
			EventDescription: "",
			IsActive: true,
		},
	});
	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};
	const onSubmit: SubmitHandler<PhaseEventType> = async (data: any) => {
		setIsSubmitLoading(true);
		const response = await upsertPhaseEventType(data);
		if (response.ok) {
			setIsSubmitLoading(false);
			setOpen(true);
			onHide();
			refreshData();
		} else {
			setError(response.message);
		}
		setIsSubmitLoading(false);
	};
	useEffect(() => {
		if (show) {
			if (phaseEvent) {
				reset(phaseEvent);
			} else {
				reset({
					PhaseEventTypeId: 0,
					EventDescription: "",
					IsActive: true,
				});
			}
		}
	}, [show, phaseEvent, reset]);
	return (
		<>
			<MesfModal
				open={show}
				handleClose={onHide}
				maxWidth="md"
				aria-labelledby="contained-modal-title-vcenter"
				title={`${phaseEvent ? "EDIT" : "NEW"} PHASE EVENT`}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
					>
						<Grid container spacing={1}>
							<Grid item xs={12} md={12}>
								<TextField
									{...register("EventDescription", { required: true })}
									label="Event Description"
									variant="outlined"
									error={!!errors.EventDescription}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											checkboxValue={!!field.value}
											onCheckboxChange={field.onChange}
											label="Enabled"
											className={classes.checkbox}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={onHide}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<MuiAlert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleClose}
				>
					{phaseEvent
						? "The element was updated successfully"
						: "The element was created successfully"}
				</MuiAlert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
